// hooks/useSienaguard.ts
import { useState, useEffect } from 'react';
import {
  getActiveServices,
  getServiceDetail,
  SienaguardService,
  SienaguardServiceDetail,
  ScriptRun,
} from '../api/sienaguardService';

export interface FoundDomain {
  domain: string;
  serviceDomain: string;
  runDate: string;
}

const useSienaguard = () => {
  const [services, setServices] = useState<SienaguardService[]>([]);
  const [serviceDetailsMap, setServiceDetailsMap] =
    useState<Record<string, SienaguardServiceDetail>>({});
  const [foundDomains, setFoundDomains] = useState<FoundDomain[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /* ──────────── Carga (y recarga) principal ──────────── */
  const refreshSienaguard = async () => {
    setLoading(true);
    try {
      const activeServices = await getActiveServices();
      setServices(activeServices);

      /* 1️⃣ Pide en paralelo el detalle de cada servicio */
      const detailArray = await Promise.all(
        activeServices.map((s) => getServiceDetail(s.id))
      );

      /* 2️⃣ Construye map id→detalle y lista de dominios */
      const detailsMap: Record<string, SienaguardServiceDetail> = {};
      const domainsSet = new Set<string>(); // evita duplicados
      const allDomains: FoundDomain[] = [];

      detailArray.forEach((detail, idx) => {
        const svc = activeServices[idx];
        detailsMap[svc.id] = detail;

        /* a) runs → found_domains */
        detail.runs?.forEach((run: ScriptRun) => {
          run.found_domains?.forEach((fd) => {
            const key = `${fd.domain}|${run.run_date}`;
            if (!domainsSet.has(key)) {
              domainsSet.add(key);
              allDomains.push({
                domain: fd.domain,
                serviceDomain: svc.domain,
                runDate: run.run_date,
              });
            }
          });
        });

        /* b) typosquatting_info */
        detail.typosquatting_info?.forEach((t) => {
          const key = `${t.fake_domain}|${detail.created_at}`;
          if (!domainsSet.has(key)) {
            domainsSet.add(key);
            allDomains.push({
              domain: t.fake_domain,
              serviceDomain: svc.domain,
              runDate: detail.created_at,
            });
          }
        });

        /* c) names_tld_info (opcional) */
        detail.names_tld_info?.forEach((n) => {
          const key = `${n.domain}|${detail.created_at}`;
          if (!domainsSet.has(key)) {
            domainsSet.add(key);
            allDomains.push({
              domain: n.domain,
              serviceDomain: svc.domain,
              runDate: detail.created_at,
            });
          }
        });
      });

      setServiceDetailsMap(detailsMap);
      setFoundDomains(allDomains);
      setError(null);
    } catch (err) {
      console.error('Error fetching Sienaguard data:', err);
      setError('Error al obtener los datos de los servicios.');
    } finally {
      setLoading(false);
    }
  };

  /* ──────────── Refrescar un único servicio ──────────── */
  const refreshServiceDetailFor = async (serviceId: string) => {
    try {
      const detail = await getServiceDetail(serviceId);
      setServiceDetailsMap((prev) => ({ ...prev, [serviceId]: detail }));
      await refreshSienaguard();
    } catch (err) {
      console.error(`Error refreshing service ${serviceId}:`, err);
    }
  };

  useEffect(() => {
    refreshSienaguard();
  }, []);

  return {
    services,
    serviceDetailsMap,
    foundDomains,
    loading,
    error,
    refreshSienaguard,
    refreshServiceDetailFor,
  };
};

export default useSienaguard;
